// src/pages/Admin/Shipments.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, TextField, InputAdornment, Avatar,
  Select, FormControl, InputLabel, MenuItem, Tooltip, LinearProgress, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import {
  Search, Refresh, Visibility, Edit, LocalShipping,
  TrendingUp, CheckCircle, Warning, Add, Download,
  Delete, CloudUpload, Description, NotificationsActive, WhatsApp, Email,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { formatDate, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const EMPTY_SHIPMENT = {
  enquiry_id:          '',
  container_number:    '',
  vessel_name:         '',
  bl_number:           '',
  port_of_loading:     '',
  port_of_discharge:   '',
  expected_departure:  '',
  expected_arrival:    '',
  status:              'pending',
  tracking_url:        '',
  notes:               '',
  shipping_line:       '',
  container_type:      '',
  temperature_control: '',
  transit_days:        '',
};

const VALID_STATUSES = ['pending', 'shipped', 'in_transit', 'arrived', 'delivered'];

// ─────────────────────────────────────────────────────────────────────────────
// ShipmentFormFields — MUST be defined OUTSIDE AdminShipments.
// If defined inside the parent, React treats it as a new component type on every
// render (keystroke) → unmounts + remounts → input loses focus after 1 char.
// Receiving everything as props keeps its identity stable across parent renders.
// ─────────────────────────────────────────────────────────────────────────────
const ShipmentFormFields = ({ isCreate, shipmentForm, handleFormChange, enquiries, docFile, setDocFile }) => (
  <Grid container spacing={2} sx={{ mt: 0.5 }}>
    {isCreate && (
      <Grid item xs={12}>
        <FormControl fullWidth size="small" required>
          <InputLabel>Select Enquiry *</InputLabel>
          <Select name="enquiry_id" label="Select Enquiry *" value={shipmentForm.enquiry_id}
            onChange={handleFormChange}>
            {enquiries.map(e => (
              <MenuItem key={e.id} value={e.id}>
                #{e.id} — {e.buyerName || e.user?.company_name || 'Unknown'} — {e.product || 'Fruit'} [{e.status}]
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    )}
    <Grid item xs={12} sm={6}>
      <TextField fullWidth size="small" required label="Container Number *"
        name="container_number" placeholder="e.g. MSCU1234567"
        value={shipmentForm.container_number} onChange={handleFormChange} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth size="small" label="BL Number (Bill of Lading)"
        name="bl_number" placeholder="e.g. MSCUABCD123456"
        value={shipmentForm.bl_number} onChange={handleFormChange} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth size="small" label="Vessel Name" name="vessel_name"
        placeholder="e.g. MSC ANNA"
        value={shipmentForm.vessel_name} onChange={handleFormChange} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <FormControl fullWidth size="small">
        <InputLabel>Status</InputLabel>
        <Select name="status" label="Status" value={shipmentForm.status}
          onChange={handleFormChange}>
          {VALID_STATUSES.map(s => (
            <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
              {s.replace('_', ' ')}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth size="small" label="Port of Loading (Origin)"
        name="port_of_loading" placeholder="e.g. JNPT Mumbai, India"
        value={shipmentForm.port_of_loading} onChange={handleFormChange} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth size="small" label="Port of Discharge (Destination)"
        name="port_of_discharge" placeholder="e.g. Jebel Ali Port, Dubai, UAE"
        value={shipmentForm.port_of_discharge} onChange={handleFormChange} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth size="small" type="date" label="Expected Departure"
        name="expected_departure" value={shipmentForm.expected_departure}
        onChange={handleFormChange} InputLabelProps={{ shrink: true }} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth size="small" type="date" label="Expected Arrival (ETA)"
        name="expected_arrival" value={shipmentForm.expected_arrival}
        onChange={handleFormChange} InputLabelProps={{ shrink: true }} />
    </Grid>

    {/* ── New logistics fields ── */}
    <Grid item xs={12} sm={6}>
      <TextField fullWidth size="small" label="Shipping Line"
        name="shipping_line" placeholder="e.g. Maersk, MSC, CMA CGM"
        value={shipmentForm.shipping_line} onChange={handleFormChange} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <FormControl fullWidth size="small">
        <InputLabel>Container Type</InputLabel>
        <Select name="container_type" label="Container Type"
          value={shipmentForm.container_type} onChange={handleFormChange}>
          <MenuItem value="">— Select —</MenuItem>
          <MenuItem value="20ft Standard">20ft Standard (Dry)</MenuItem>
          <MenuItem value="40ft Standard">40ft Standard (Dry)</MenuItem>
          <MenuItem value="40ft High Cube">40ft High Cube</MenuItem>
          <MenuItem value="20ft Reefer">20ft Reefer</MenuItem>
          <MenuItem value="40ft Reefer">40ft Reefer</MenuItem>
          <MenuItem value="40ft High Cube Reefer">40ft High Cube Reefer</MenuItem>
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth size="small" label="Temperature Control"
        name="temperature_control" placeholder="e.g. 2-4°C, 8-10°C, Ambient"
        value={shipmentForm.temperature_control} onChange={handleFormChange} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth size="small" label="Transit Days (approx.)"
        name="transit_days" placeholder="e.g. 12, 18-22"
        value={shipmentForm.transit_days} onChange={handleFormChange} />
    </Grid>

    <Grid item xs={12}>
      <TextField fullWidth size="small" label="Tracking URL (optional)"
        name="tracking_url" placeholder="https://track.msc.com/..."
        value={shipmentForm.tracking_url} onChange={handleFormChange} />
    </Grid>
    <Grid item xs={12}>
      <TextField fullWidth size="small" label="Notes / Remarks (optional)" multiline rows={2}
        name="notes" value={shipmentForm.notes} onChange={handleFormChange} />
    </Grid>

    {/* Shipping Document Upload */}
    <Grid item xs={12}>
      <Button component="label" variant="outlined" startIcon={<CloudUpload />} fullWidth
        sx={{ borderStyle: 'dashed', py: 1.5 }}>
        {docFile
          ? <><Description sx={{ mr: 1 }} />{docFile.name}</>
          : 'Upload Shipping Document (PDF / image — optional)'}
        <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
          onChange={e => setDocFile(e.target.files[0] || null)} />
      </Button>
      {docFile && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {(docFile.size / 1024).toFixed(1)} KB selected
        </Typography>
      )}
    </Grid>
  </Grid>
);

const AdminShipments = () => {
  const [shipments,     setShipments]     = useState([]);
  const [enquiries,     setEnquiries]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [tabValue,      setTabValue]      = useState(0);
  const [typeFilter,    setTypeFilter]    = useState('all');
  const [selected,      setSelected]      = useState(null);
  const [viewDialog,    setViewDialog]    = useState(false);
  const [editDialog,    setEditDialog]    = useState(false);
  const [createDialog,  setCreateDialog]  = useState(false);
  const [deleteDialog,  setDeleteDialog]  = useState(false);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [shipmentForm,  setShipmentForm]  = useState(EMPTY_SHIPMENT);
  const [docFile,       setDocFile]       = useState(null);  // shipping document
  const [creating,      setCreating]      = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [deleting,      setDeleting]      = useState(false);
  const [notifyDialog,  setNotifyDialog]  = useState(false);
  const [notifyTarget,  setNotifyTarget]  = useState(null);
  const [notifyMsg,     setNotifyMsg]     = useState('');
  const [notifyResult,  setNotifyResult]  = useState(null);
  const [notifying,     setNotifying]     = useState(false);

  const TAB_STATUS_MAP = ['all', 'pending', 'shipped', 'in_transit', 'arrived', 'delivered'];

  useEffect(() => {
    fetchShipments();
    fetchEnquiries();
  }, []);

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllShipments();
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.shipments || response.data?.data || []);
      setShipments(data);
    } catch (error) {
      console.error('Error fetching shipments:', error);
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnquiries = async () => {
    try {
      const r = await adminAPI.getAllEnquiries();
      const data = Array.isArray(r.data) ? r.data : (r.data?.enquiries || []);
      setEnquiries(data.filter(e => !['cancelled', 'completed'].includes(e.status)));
    } catch (e) {
      console.error('fetchEnquiries error', e);
    }
  };

  // ✅ FIX: stable handler — no new function ref on each render → cursor won't jump
  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setShipmentForm(prev => ({ ...prev, [name]: value }));
  }, []);

  // ── Create ──────────────────────────────────────────────────────────────────
  const handleCreateShipment = async () => {
    if (!shipmentForm.enquiry_id) { toast.error('Please select an enquiry'); return; }
    if (!shipmentForm.container_number.trim()) { toast.error('Please enter a container number'); return; }
    try {
      setCreating(true);
      const fd = new FormData();
      Object.entries(shipmentForm).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (docFile) fd.append('document', docFile);
      await adminAPI.createShipment(fd);
      toast.success('Shipment created successfully!');
      fetchShipments();
      setCreateDialog(false);
      setShipmentForm(EMPTY_SHIPMENT);
      setDocFile(null);
    } catch (err) {
      console.error('Create shipment error', err);
      toast.error(err?.data?.error || err?.message || 'Failed to create shipment');
    } finally {
      setCreating(false);
    }
  };

  // ── Full Edit ────────────────────────────────────────────────────────────────
  const openEdit = (s) => {
    setShipmentForm({
      enquiry_id:          s.enquiry_id        || '',
      container_number:    s.container_number  || '',
      vessel_name:         s.vessel_name       || '',
      bl_number:           s.bl_number         || '',
      port_of_loading:     s.port_of_loading   || s.departure_port    || '',
      port_of_discharge:   s.port_of_discharge || s.destination_port  || '',
      expected_departure:  s.expected_departure ? s.expected_departure.slice(0, 10) : '',
      expected_arrival:    s.expected_arrival   ? s.expected_arrival.slice(0, 10)   : '',
      status:              s.status            || 'pending',
      tracking_url:        s.tracking_url      || '',
      notes:               s.notes             || '',
      shipping_line:       s.shipping_line       || '',
      container_type:      s.container_type      || '',
      temperature_control: s.temperature_control || '',
      transit_days:        s.transit_days        || '',
    });
    setDocFile(null);
    setSelected(s);
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!shipmentForm.container_number.trim()) { toast.error('Container number is required'); return; }
    try {
      setSaving(true);
      const fd = new FormData();
      Object.entries(shipmentForm).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      if (docFile) fd.append('document', docFile);
      await adminAPI.updateFullShipment(selected.id, fd);
      toast.success('Shipment updated!');
      fetchShipments();
      setEditDialog(false);
    } catch (err) {
      console.error('Edit shipment error', err);
      toast.error(err?.data?.error || err?.message || 'Failed to update shipment');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      setDeleting(true);
      await adminAPI.deleteShipment(deleteTarget.id);
      toast.success('Shipment deleted');
      setDeleteDialog(false);
      setDeleteTarget(null);
      fetchShipments();
    } catch (err) {
      toast.error('Failed to delete shipment');
    } finally {
      setDeleting(false);
    }
  };

  // ── Notify Buyer ─────────────────────────────────────────────────────────────
  const openNotify = (s) => {
    setNotifyTarget(s);
    setNotifyResult(null);
    const defaultMsg = `Your shipment ${s.container_number || `#${s.id}`} status has been updated to: ${(s.status || 'pending').replace('_', ' ').toUpperCase()}. Container: ${s.container_number || '—'}, Vessel: ${s.vessel_name || '—'}, ETA: ${s.expected_arrival ? new Date(s.expected_arrival).toLocaleDateString() : 'TBD'}.`;
    setNotifyMsg(defaultMsg);
    setNotifyDialog(true);
  };

  const handleNotify = async () => {
    if (!notifyTarget) return;
    setNotifying(true);
    try {
      const buyerId = notifyTarget.user?.id || notifyTarget.buyer_id || notifyTarget.enquiry?.buyer_id || notifyTarget.enquiry?.user_id;
      const res = await adminAPI.notifyBuyer({
        buyer_id: buyerId,
        message: notifyMsg,
        type: 'shipment_update',
      });
      setNotifyResult(res.data);
      toast.success('Notification details ready!');
    } catch (err) {
      toast.error('Failed to get notification details');
    } finally {
      setNotifying(false);
    }
  };

  // ── ETA Countdown (same logic as buyer page) ─────────────────────────────────
  const getEtaInfo = (arrival) => {
    if (!arrival) return null;
    const days = Math.ceil((new Date(arrival) - new Date()) / 86400000);
    if (days < 0)  return { label: `${Math.abs(days)}d overdue`, color: 'error' };
    if (days === 0) return { label: 'Arriving today!', color: 'success' };
    return { label: `${days} days to arrival`, color: days <= 5 ? 'warning' : 'info' };
  };

  // ── Document view ────────────────────────────────────────────────────────────
  const getDocUrl = (s) => {
    const raw = s?.document_url || s?.document_path || s?.shipping_document || s?.doc_url;
    if (!raw) return null;
    if (raw.startsWith('http')) return raw;
    return `${API_BASE}/${raw.replace(/^\//, '')}`;
  };

  const handleExport = () => {
    if (shipments.length === 0) { toast.error('No shipments to export'); return; }
    const headers = ['ID','Enquiry ID','Container No','Vessel','BL Number','Port Loading',
      'Port Discharge','Departure','Arrival','Status','Tracking URL','Notes'];
    const rows = shipments.map(s => [
      s.id, s.enquiry_id || s.enquiry?.id || '',
      s.container_number || '', s.vessel_name || '', s.bl_number || '',
      s.port_of_loading || '', s.port_of_discharge || '',
      s.expected_departure || s.departure_date || '',
      s.expected_arrival || s.arrival_date || '',
      s.status || '', s.tracking_url || '', (s.notes || '').replace(/,/g, ';'),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `shipments_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${shipments.length} shipments`);
  };

  const stats = {
    total:     shipments.length,
    inTransit: shipments.filter(s => s.status === 'in_transit').length,
    delivered: shipments.filter(s => s.status === 'delivered').length,
    delayed:   shipments.filter(s => s.status === 'delayed').length,
  };

  const statusTab = TAB_STATUS_MAP[tabValue];
  const filteredShipments = shipments.filter(s => {
    const matchesStatus = statusTab === 'all' || s.status === statusTab;
    const matchesType   = typeFilter === 'all' || (s.container_type || '').includes(typeFilter);
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      !q ||
      (s.container_number || '').toLowerCase().includes(q) ||
      (s.vessel_name      || '').toLowerCase().includes(q) ||
      (s.enquiry?.id      || '').toString().includes(q) ||
      (s.enquiry_id       || '').toString().includes(q) ||
      (s.user?.company_name || '').toLowerCase().includes(q);
    return matchesStatus && matchesType && matchesSearch;
  });

  if (loading) return (
    <Container sx={{ py: 4 }}>
      <LinearProgress sx={{ mb: 2 }} />
      <Typography>Loading shipments...</Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Shipments Management</Typography>
          <Typography variant="body1" color="text.secondary">
            Track and manage all shipments and containers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Download />}
            onClick={handleExport}>Export CSV</Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={fetchShipments}>Refresh</Button>
          <Button variant="contained" color="success" startIcon={<Add />}
            onClick={() => { setShipmentForm(EMPTY_SHIPMENT); setDocFile(null); setCreateDialog(true); }}>
            Create Shipment
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Shipments', value: stats.total,     color: '#2196f3', icon: <LocalShipping /> },
          { label: 'In Transit',      value: stats.inTransit, color: '#00bcd4', icon: <TrendingUp />,  textColor: 'info.main' },
          { label: 'Delivered',       value: stats.delivered, color: '#4caf50', icon: <CheckCircle />, textColor: 'success.main' },
          { label: 'Delayed',         value: stats.delayed,   color: '#ff9800', icon: <Warning />,     textColor: 'warning.main' },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                    <Typography variant="h4" fontWeight="bold" color={s.textColor || 'text.primary'}>
                      {s.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: `${s.color}20`, color: s.color }}>{s.icon}</Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable">
          {['All', 'Pending', 'Shipped', 'In Transit', 'Arrived', 'Delivered'].map((label, i) => (
            <Tab key={i} label={label} />
          ))}
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField fullWidth size="small"
              placeholder="Search by container, enquiry ID, vessel..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Container Type</InputLabel>
              <Select label="Container Type" value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="Reefer">Reefer</MenuItem>
                <MenuItem value="Dry">Dry</MenuItem>
                <MenuItem value="High Cube">High Cube</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                {['Container', 'Enquiry', 'Vessel', 'Ports', 'Status', 'ETA', 'Doc', 'Actions'].map(h => (
                  <TableCell key={h}><Typography variant="subtitle2" fontWeight="bold">{h}</Typography></TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredShipments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <LocalShipping sx={{ fontSize: 60, color: 'text.secondary', display: 'block', mx: 'auto', mb: 1 }} />
                    <Typography color="text.secondary" gutterBottom>
                      {shipments.length === 0 ? 'No shipments yet' : 'No shipments match the filter'}
                    </Typography>
                    {shipments.length === 0 && (
                      <Button variant="contained" color="success" startIcon={<Add />}
                        onClick={() => setCreateDialog(true)} sx={{ mt: 1 }}>
                        Create First Shipment
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : filteredShipments.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {s.container_number || `SHIP-${s.id}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.bl_number ? `BL: ${s.bl_number}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">#{s.enquiry_id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.user?.company_name || s.enquiry?.buyerName || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{s.vessel_name || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{s.port_of_loading || s.departure_port || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      → {s.port_of_discharge || s.destination_port || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={(s.status || 'pending').replace('_', ' ')} size="small"
                      color={getStatusColor(s.status)} sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const eta = getEtaInfo(s.expected_arrival || s.estimated_delivery || s.eta);
                      return eta ? (
                        <Box>
                          <Chip label={eta.label} size="small" color={eta.color} variant="outlined" />
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatDate(s.expected_arrival || s.estimated_delivery || s.eta)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(s.expected_arrival || s.estimated_delivery || s.eta) || '—'}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {getDocUrl(s) ? (
                      <Tooltip title="View Document">
                        <IconButton size="small" color="primary"
                          onClick={() => window.open(getDocUrl(s), '_blank')}>
                          <Description />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" color="primary"
                          onClick={() => { setSelected(s); setViewDialog(true); }}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Shipment">
                        <IconButton size="small" color="info" onClick={() => openEdit(s)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Notify Buyer">
                        <IconButton size="small" color="secondary" onClick={() => openNotify(s)}>
                          <NotificationsActive />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Shipment">
                        <IconButton size="small" color="error"
                          onClick={() => { setDeleteTarget(s); setDeleteDialog(true); }}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredShipments.length} of {shipments.length} shipments
          </Typography>
        </Box>
      </Paper>

      {/* ── Create Dialog ── */}
      <Dialog open={createDialog}
        onClose={() => { setCreateDialog(false); setShipmentForm(EMPTY_SHIPMENT); setDocFile(null); }}
        maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping color="success" />
            <Typography variant="h6">Create New Shipment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <ShipmentFormFields isCreate
            shipmentForm={shipmentForm} handleFormChange={handleFormChange}
            enquiries={enquiries} docFile={docFile} setDocFile={setDocFile} />
          <Alert severity="info" sx={{ mt: 2 }}>
            The linked enquiry status will automatically update to <strong>Confirmed</strong> once the shipment is created.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialog(false); setShipmentForm(EMPTY_SHIPMENT); setDocFile(null); }}>
            Cancel
          </Button>
          <Button variant="contained" color="success" startIcon={<LocalShipping />}
            onClick={handleCreateShipment}
            disabled={creating || !shipmentForm.enquiry_id || !shipmentForm.container_number.trim()}>
            {creating ? 'Creating...' : 'Create Shipment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Dialog ── */}
      <Dialog open={editDialog} onClose={() => { setEditDialog(false); setDocFile(null); }}
        maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit color="info" />
            <Typography variant="h6">Edit Shipment — {selected?.container_number || `#${selected?.id}`}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <ShipmentFormFields isCreate={false}
            shipmentForm={shipmentForm} handleFormChange={handleFormChange}
            enquiries={enquiries} docFile={docFile} setDocFile={setDocFile} />
          {selected && getDocUrl(selected) && (
            <Alert severity="info" sx={{ mt: 2 }} icon={<Description />}>
              Existing document:{' '}
              <Button size="small" onClick={() => window.open(getDocUrl(selected), '_blank')}>
                View current document
              </Button>
              {' '}(upload a new file above to replace it)
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialog(false); setDocFile(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── View Dialog ── */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>Shipment Details — {selected.container_number || `#${selected.id}`}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {[
                  ['Container',         selected.container_number],
                  ['BL Number',         selected.bl_number],
                  ['Vessel',            selected.vessel_name],
                  ['Shipping Line',     selected.shipping_line],
                  ['Container Type',    selected.container_type],
                  ['Temp. Control',     selected.temperature_control],
                  ['Transit Days',      selected.transit_days ? `${selected.transit_days} days` : null],
                  ['Loading Port',      selected.port_of_loading || selected.departure_port],
                  ['Discharge Port',    selected.port_of_discharge || selected.destination_port],
                  ['Status',            (selected.status || '').replace('_', ' ')],
                  ['Dep. Date',         formatDate(selected.expected_departure)],
                  ['ETA',               formatDate(selected.expected_arrival || selected.estimated_delivery || selected.eta)],
                  ['Tracking',          selected.tracking_url],
                  ['Buyer',             selected.user?.company_name || selected.enquiry?.buyerName],
                  ['Notes',             selected.notes],
                ].map(([label, value]) => value ? (
                  <Box key={label} sx={{ display: 'flex', gap: 2, borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ width: 130, flexShrink: 0 }}>{label}:</Typography>
                    <Typography variant="body2">{value}</Typography>
                  </Box>
                ) : null)}

                {getDocUrl(selected) && (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ width: 130, flexShrink: 0 }}>
                      Shipping Doc:
                    </Typography>
                    <Button size="small" variant="outlined" startIcon={<Description />}
                      onClick={() => window.open(getDocUrl(selected), '_blank')}>
                      View / Download
                    </Button>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialog(false)}>Close</Button>
              <Button variant="outlined" color="error"
                onClick={() => { setViewDialog(false); setDeleteTarget(selected); setDeleteDialog(true); }}>
                Delete
              </Button>
              <Button variant="contained"
                onClick={() => { setViewDialog(false); openEdit(selected); }}>
                Edit Shipment
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Notify Buyer Dialog ── */}
      <Dialog open={notifyDialog} onClose={() => { setNotifyDialog(false); setNotifyResult(null); }}
        maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsActive color="secondary" />
            <Typography variant="h6">Notify Buyer — Shipment Update</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Compose a message for the buyer. You'll get a WhatsApp link and buyer email to send it.
            </Alert>
            <TextField
              fullWidth multiline rows={4}
              label="Message"
              value={notifyMsg}
              onChange={e => setNotifyMsg(e.target.value)}
              sx={{ mb: 2 }}
            />
            {notifyResult && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                <Alert severity="success">Notification links ready — use one of the options below:</Alert>
                {notifyResult.whatsapp_link && (
                  <Button variant="contained" color="success" startIcon={<WhatsApp />} fullWidth
                    onClick={() => window.open(notifyResult.whatsapp_link, '_blank')}>
                    Open WhatsApp Chat
                  </Button>
                )}
                {notifyResult.buyer_email && (
                  <Button variant="outlined" color="primary" startIcon={<Email />} fullWidth
                    onClick={() => window.open(`mailto:${notifyResult.buyer_email}?subject=Shipment Update&body=${encodeURIComponent(notifyMsg)}`, '_blank')}>
                    Send Email to {notifyResult.buyer_email}
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Click WhatsApp to open a pre-filled chat, or Email to open your mail client
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNotifyDialog(false); setNotifyResult(null); }}>Close</Button>
          {!notifyResult && (
            <Button variant="contained" color="secondary" startIcon={<NotificationsActive />}
              onClick={handleNotify} disabled={notifying || !notifyMsg.trim()}>
              {notifying ? 'Loading...' : 'Get Notification Links'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Shipment</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            Are you sure you want to delete shipment{' '}
            <strong>{deleteTarget?.container_number || `#${deleteTarget?.id}`}</strong>?
            This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminShipments;
